// Figma: the "Table" component shared by the Resident Action Feed and Campaigns tables
// (nodes 18034:1411 and 18034:1563) — title+description+Export header, search+filter row,
// sortable column headers, body rows, and pagination footer. LC2 rendered both of these
// with MUI X DataGrid Pro (a licensed package — see StyledTable in
// commons/components/@extended/CustomTables.js); this new design's table doesn't use any
// DataGrid-only features (no resizable columns, no filter panel), so it's rebuilt here as
// a plain styled table to avoid pulling in a licensed dependency for something this simple.
// Search/sort/pagination are client-side over whatever `rows` the caller already fetched
// via the real (ported) API hooks — the data layer is unchanged, only the chrome is new.
//
// `showFilterLabel`, `noSortFields`, and `showGridLines` are opt-in (default off) so the
// other ~10 tables built on this shell keep their existing look; Reports/ReportsSection.js
// (Figma node 18777:3043) is the only caller turning them on, per its own frame's icon+label
// filter button, unsortable action column, and full row/column grid lines.
import React, {useMemo, useRef, useState} from 'react';
import {Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography} from '@mui/material';
import {tokens} from '../../../themes/ThemeCustomization';
import TableFilterPanel, {matchesConditions} from './TableFilterPanel';

import exportIcon from '../../../assets/shell/table-export-icon.svg';
import searchIcon from '../../../assets/shell/table-search-icon.svg';
import filterIcon from '../../../assets/shell/table-filter-icon.svg';
import sortIcon from '../../../assets/shell/table-sort-icon.svg';
import paginationDropdownIcon from '../../../assets/shell/pagination-dropdown-icon.svg';

const PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const stripHtml = (value) => {
    if (typeof value !== 'string' || !value.includes('<')) return value;
    return new DOMParser().parseFromString(value, 'text/html').body.textContent;
};

const csvCell = (value) => {
    const text = String(stripHtml(value) ?? '').replace(/\s+/g, ' ').trim();
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

// Default Export action: downloads the currently searched/sorted rows as CSV.
const downloadCsv = (title, columns, rows) => {
    const exportColumns = columns.filter((col) => col.exportable !== false);
    const lines = [exportColumns.map((col) => csvCell(col.headerName)).join(',')];
    rows.forEach((row) => {
        lines.push(exportColumns.map((col) => csvCell(col.exportValue ? col.exportValue(row) : row[col.field])).join(','));
    });
    const blob = new Blob([`﻿${lines.join('\r\n')}`], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(title || 'export').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const IconButton = ({icon, alt, label, onClick, sx}) => (
    <Box
        component="button"
        type="button"
        onClick={onClick}
        sx={{
            border: `1px solid ${tokens.color.neutral200}`,
            borderRadius: '6px',
            backgroundColor: tokens.color.white,
            height: 36,
            px: label ? 1.5 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            cursor: 'pointer',
            ...sx
        }}
    >
        <Box component="img" src={icon} alt={label ? '' : alt} sx={{width: 16, height: 16}} />
        {label && (
            <Typography variant="body2" fontWeight={500}>
                {label}
            </Typography>
        )}
    </Box>
);

const DataTableShell = ({
    title,
    description,
    columns,
    rows,
    getRowId,
    searchPlaceholder = 'Search',
    onExport,
    onRowClick,
    showFilterLabel = false,
    noSortFields = [],
    showGridLines = false,
    showFooter = true,
    filterable = false
}) => {
    const filterButtonRef = useRef(null);
    const [filterOpen, setFilterOpen] = useState(false);
    const [conditions, setConditions] = useState([]);
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState(null); // {field, direction}
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);

    const filterColumns = useMemo(() => columns.filter((col) => col.exportable !== false && col.headerName), [columns]);

    const filteredRows = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return rows.filter(
            (row) =>
                (!needle || columns.some((col) => String(row[col.field] ?? '').toLowerCase().includes(needle))) &&
                matchesConditions(conditions, (field) => row[field])
        );
    }, [rows, search, columns, conditions]);

    const sortedRows = useMemo(() => {
        if (!sort) return filteredRows;
        const {field, direction} = sort;
        const copy = [...filteredRows];
        copy.sort((a, b) => {
            const av = a[field];
            const bv = b[field];
            const numeric = typeof av === 'number' && typeof bv === 'number';
            const cmp = numeric ? av - bv : String(av ?? '').localeCompare(String(bv ?? ''));
            return direction === 'asc' ? cmp : -cmp;
        });
        return copy;
    }, [filteredRows, sort]);

    const pageCount = Math.max(1, Math.ceil(sortedRows.length / pageSize));
    const clampedPage = Math.min(page, pageCount - 1);
    const pageRows = sortedRows.slice(clampedPage * pageSize, clampedPage * pageSize + pageSize);
    const rangeStart = sortedRows.length === 0 ? 0 : clampedPage * pageSize + 1;
    const rangeEnd = Math.min(sortedRows.length, clampedPage * pageSize + pageSize);

    const toggleSort = (field) => {
        setSort((prev) => {
            if (!prev || prev.field !== field) return {field, direction: 'asc'};
            if (prev.direction === 'asc') return {field, direction: 'desc'};
            return null;
        });
    };

    return (
        <Box sx={{border: `1px solid ${tokens.color.neutral200}`, borderRadius: '6px', backgroundColor: tokens.color.white, overflow: 'hidden'}}>
            {title && (
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{p: 2, borderBottom: `1px solid ${tokens.color.neutral200}`}}>
                    <Stack spacing={0.25}>
                        <Typography variant="subtitle1" fontWeight={600}>
                            {title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {description}
                        </Typography>
                    </Stack>
                    <Box
                        component="button"
                        type="button"
                        onClick={onExport || (() => downloadCsv(title, columns, sortedRows))}
                        sx={{
                            border: `1px solid ${tokens.color.neutral200}`,
                            borderRadius: '6px',
                            backgroundColor: tokens.color.white,
                            height: 36,
                            px: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            cursor: 'pointer'
                        }}
                    >
                        <Box component="img" src={exportIcon} alt="" sx={{width: 16, height: 16}} />
                        <Typography variant="body2" fontWeight={500}>
                            Export
                        </Typography>
                    </Box>
                </Stack>
            )}

            <Stack direction="row" spacing={1} sx={{p: 2, borderBottom: `1px solid ${tokens.color.neutral200}`}}>
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        border: `1px solid ${tokens.color.neutral200}`,
                        borderRadius: '6px',
                        height: 36,
                        px: 1.5
                    }}
                >
                    <Box component="img" src={searchIcon} alt="" sx={{width: 14, height: 14}} />
                    <Box
                        component="input"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(0);
                        }}
                        placeholder={searchPlaceholder}
                        sx={{border: 'none', outline: 'none', flex: 1, fontSize: '14px', fontFamily: 'inherit', backgroundColor: 'transparent'}}
                    />
                </Box>
                <Box ref={filterButtonRef} sx={{position: 'relative', display: 'flex'}}>
                    <IconButton
                        icon={filterIcon}
                        alt="Filter"
                        label={showFilterLabel ? 'Filter' : undefined}
                        onClick={filterable ? () => setFilterOpen((open) => !open) : undefined}
                        sx={conditions.length ? {borderColor: tokens.color.sky600} : undefined}
                    />
                    {filterable && conditions.length > 0 && (
                        <Box sx={{position: 'absolute', top: -4, right: -4, width: 8, height: 8, borderRadius: '50%', backgroundColor: tokens.color.sky600}} />
                    )}
                </Box>
                {filterable && filterOpen && (
                    <TableFilterPanel
                        open
                        anchorEl={filterButtonRef.current}
                        onClose={() => setFilterOpen(false)}
                        columns={filterColumns}
                        conditions={conditions}
                        onApply={(next) => {
                            setConditions(next);
                            setPage(0);
                            setFilterOpen(false);
                        }}
                    />
                )}
            </Stack>

            <Box sx={{overflowX: 'auto'}}>
                <Table sx={{minWidth: '100%'}}>
                    <TableHead>
                        <TableRow sx={{backgroundColor: tokens.color.neutral50}}>
                            {columns.map((col, index) => {
                                const sortable = !noSortFields.includes(col.field);
                                return (
                                    <TableCell
                                        key={col.field}
                                        onClick={sortable ? () => toggleSort(col.field) : undefined}
                                        sx={{
                                            cursor: sortable ? 'pointer' : 'default',
                                            whiteSpace: 'nowrap',
                                            borderBottom: 'none',
                                            borderRight: showGridLines && index < columns.length - 1 ? `1px solid ${tokens.color.neutral200}` : 'none',
                                            color: tokens.color.neutral600 || '#525252',
                                            fontWeight: 500,
                                            fontSize: '14px',
                                            width: col.width
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                            <span>{col.headerName}</span>
                                            {sortable && <Box component="img" src={sortIcon} alt="" sx={{width: 14, height: 14}} />}
                                        </Stack>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pageRows.map((row) => (
                            <TableRow
                                key={getRowId(row)}
                                onClick={onRowClick ? () => onRowClick(row) : undefined}
                                sx={{borderTop: `1px solid ${tokens.color.neutral200}`, cursor: onRowClick ? 'pointer' : 'default'}}
                                hover={Boolean(onRowClick)}
                            >
                                {columns.map((col, index) => (
                                    <TableCell
                                        key={col.field}
                                        sx={{
                                            fontSize: '14px',
                                            verticalAlign: 'middle',
                                            borderRight: showGridLines && index < columns.length - 1 ? `1px solid ${tokens.color.neutral200}` : 'none',
                                            borderBottom: 'none'
                                        }}
                                    >
                                        {col.renderCell ? col.renderCell(row) : row[col.field]}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                        {pageRows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={columns.length} sx={{textAlign: 'center', py: 4, color: 'text.secondary'}}>
                                    No results.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Box>

            {showFooter && (
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{backgroundColor: tokens.color.neutral50, borderTop: `1px solid ${tokens.color.neutral200}`, px: 3, pt: 1.5, pb: 2}}
            >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Typography variant="body2" fontWeight={500} color={tokens.color.neutral700 || '#404040'}>
                        Showing {rangeStart}-{rangeEnd} of {sortedRows.length}
                    </Typography>
                    <Box
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            border: `1px solid ${tokens.color.neutral200}`,
                            borderRadius: '6px',
                            backgroundColor: tokens.color.white,
                            height: 32,
                            px: 1.5
                        }}
                    >
                        <Typography variant="body2" fontWeight={500}>
                            {pageSize} per page
                        </Typography>
                        <Box component="img" src={paginationDropdownIcon} alt="" sx={{width: 14, height: 14}} />
                        <Box
                            component="select"
                            aria-label="Rows per page"
                            value={pageSize}
                            onChange={(e) => {
                                setPageSize(Number(e.target.value));
                                setPage(0);
                            }}
                            sx={{position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer'}}
                        >
                            {PAGE_SIZE_OPTIONS.map((size) => (
                                <option key={size} value={size}>
                                    {size} per page
                                </option>
                            ))}
                        </Box>
                    </Box>
                </Stack>
                <Stack direction="row" spacing={1.5}>
                    <Box
                        component="button"
                        type="button"
                        disabled={clampedPage === 0}
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                        sx={{
                            border: `1px solid ${tokens.color.neutral200}`,
                            borderRadius: '6px',
                            backgroundColor: tokens.color.white,
                            height: 32,
                            px: 1.5,
                            cursor: 'pointer',
                            opacity: clampedPage === 0 ? 0.5 : 1
                        }}
                    >
                        <Typography variant="body2" fontWeight={500}>
                            Previous
                        </Typography>
                    </Box>
                    <Box
                        component="button"
                        type="button"
                        disabled={clampedPage >= pageCount - 1}
                        onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                        sx={{
                            border: `1px solid ${tokens.color.neutral200}`,
                            borderRadius: '6px',
                            backgroundColor: tokens.color.white,
                            height: 32,
                            px: 1.5,
                            cursor: 'pointer',
                            opacity: clampedPage >= pageCount - 1 ? 0.5 : 1
                        }}
                    >
                        <Typography variant="body2" fontWeight={500}>
                            Next
                        </Typography>
                    </Box>
                </Stack>
            </Stack>
            )}
        </Box>
    );
};

export default DataTableShell;
